import { Box, Divider, Grid, Stack, Typography, useTheme } from "@mui/material";
import { TempUserType } from "./usePurchaseGiftCard";
import { GiftCardType } from "@/global/store/data/types";
import React from "react";
import { useForm } from "react-hook-form";
import { TmAccountDetails } from "../TmAccountDetails/TmAccountDetails";
import { useAppStore } from "@/global/store";
import { selectUser } from "@/global/store/data/user";
import { GenericPaymentForm } from "../GenericPaymentForm/GenericPaymentForm";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import {
  GiftCardBillingSubtotal,
  GiftCardBillingSummary,
  GiftCardCheckoutModal,
  RecipientDetails,
} from "./giftCard.components";
import { CardFooter, CardHeader } from "@/shared/common/layouts/cardLayout";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useTranslation } from "react-i18next";
import { DeliveryMethodSelector } from "./DeliveryMethodSelector/DeliveryMethodSelector";

interface CheckoutGiftCardProps {
  isLoading: boolean;
  isEmbeddedCheckout: boolean;
  isCheckoutLoading: boolean;
  recipientForm: ReturnType<typeof useForm>;
  tempUser: TempUserType | null;
  selectedGiftCard: GiftCardType | null;
  onCloseTab: () => void;
  onSubmitCheckout: (data: any) => void;

  recipientFormSubmitRef: any;
  onSubmitRecipientForm: (a: any) => void;
}

export function CheckoutGiftCard(props: CheckoutGiftCardProps) {
  const theme = useTheme();
  const { md: mdDown, sm: smDown } = useCssMediaQuery("down");
  const { t } = useTranslation();
  const { giftCardConfigs } = useTeamConfig();

  const primaryButtonText = giftCardConfigs?.enableConfirmationModal
    ? t("giftCard.checkout.purchaseButtonTextBeforeConfirmation") || "Next"
    : t("giftCard.checkout.purchaseButtonText") || "Purchase";

  const checkoutGiftCardFooter = (
    <CardFooter
      primaryButtonText={primaryButtonText}
      secondaryButtonText={t("giftCard.checkout.backButtonText") || "Back"}
      onClickSecondaryButton={props.onCloseTab}
      onClickPrimaryButton={() => {
        const btn = document.getElementById("purchase-gift-submit");
        btn?.click();
      }}
      isLoadingPrimaryButton={props.isCheckoutLoading}
      isDisabledPrimaryButton={props.isCheckoutLoading}
      isDisabledSecondaryButton={props.isCheckoutLoading}
      style={{
        primaryButton: {
          width: smDown ? "100%" : "180px",
          borderRadius: "50px",
        },
        secondaryButton: {
          width: smDown ? "100%" : "180px",
          borderRadius: "50px",
        },
      }}
    />
  );

  if (props.isEmbeddedCheckout) {
    return (
      <Stack
        border={`1px solid ${theme.palette.divider}`}
        borderRadius={0.8}
        bgcolor={theme.palette.background.paper}
      >
        <Box>
          <CardHeader
            title={t("giftCard.checkout.cardTitle") || "Gift Card"}
            onClickBack={props.onCloseTab}
            onClickClose={props.onCloseTab}
            hideCloseIcon={true}
            isNavButtonDisabled={props.isCheckoutLoading}
          />
        </Box>

        <Divider />
        <Box width={"100%"}>
          <GiftCardCheckoutBody {...props} />
        </Box>
        <Divider />
        <Box>{checkoutGiftCardFooter}</Box>
      </Stack>
    );
  } else {
    return (
      <GiftCardCheckoutModal>
        <Stack height={"100%"}>
          <Box width={"100%"}>
            <CardHeader
              title={t("giftCard.checkout.cardTitle") || "Gift Card"}
              onClickBack={props.onCloseTab}
              onClickClose={props.onCloseTab}
              hideBackIcon={!mdDown ? true : false}
              hideCloseIcon={mdDown ? true : false}
              isNavButtonDisabled={props.isCheckoutLoading}
            />
          </Box>
          <Divider />
          <Box overflow={"auto"} flexGrow={1}>
            <GiftCardCheckoutBody {...props} />
          </Box>
          <Divider />
          <Box>{checkoutGiftCardFooter}</Box>
        </Stack>
      </GiftCardCheckoutModal>
    );
  }
}

function GiftCardCheckoutBody(props: CheckoutGiftCardProps) {
  const theme = useTheme();
  const { md: mdDown } = useCssMediaQuery("down");
  const user = useAppStore(selectUser);
  const { giftCardConfigs } = useTeamConfig();
  const isSaveCardEnabled = giftCardConfigs?.isCheckoutSaveCardEnabled;
  const { t } = useTranslation();

  return (
    <Box height={"100%"}>
      <Grid container height={"100%"}>
        <Grid
          item
          xs={12}
          md={6}
          borderRight={!mdDown ? `1px solid ${theme.palette.divider}` : "unset"}
          borderBottom={mdDown ? `1px solid ${theme.palette.divider}` : "unset"}
        >
          <Stack height={"100%"}>
            {user || props.tempUser ? (
              <Stack spacing={1} px={3} mt={2}>
                <Typography
                  variant="h6"
                  color={"text.primary"}
                  fontWeight={700}
                >
                  {t("accountDetails.title") || "Account Details"}
                </Typography>
                {user ? (
                  <TmAccountDetails
                    email={user.email}
                    accountId={user.accountId}
                  />
                ) : null}
                {props.tempUser ? (
                  <TmAccountDetails
                    email={props.tempUser.email}
                    accountId={props.tempUser.accountId}
                  />
                ) : null}
              </Stack>
            ) : null}

            <Stack spacing={1} mt={3} px={3}>
              <GiftCardBillingSummary
                selectedGiftCard={props.selectedGiftCard}
              />
            </Stack>

            <Box flexGrow={1} />
            {props.selectedGiftCard ? (
              <Box mt={3}>
                <GiftCardBillingSubtotal
                  selectedGiftCard={props.selectedGiftCard}
                />
              </Box>
            ) : null}
          </Stack>
        </Grid>
        <Grid item xs={12} md={6} p={3}>
          <RecipientDetails
            ref={props?.recipientFormSubmitRef}
            formMethods={props.recipientForm}
            onSubmit={props.onSubmitRecipientForm}
          />
          <DeliveryMethodSelector />
          <GenericPaymentForm
            formId="purchase-gift-checkout-form"
            submitButtonId="purchase-gift-submit"
            isPaymentDue={true}
            onSubmit={props.onSubmitCheckout}
            hideSaveCardSwitch={!isSaveCardEnabled}
            tempUser={props.tempUser}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
